import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import AppService from '../services/app';
import { Navigate } from 'react-router-dom';
import './Apps.css';

const Apps = () => {
  const { isAuthenticated, currentUser } = useAuth();
  const { apps, loading, error, fetchApps, addApp, removeApp } = useApp();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [creatingApp, setCreatingApp] = useState(false);
  const [deletingAppId, setDeletingAppId] = useState(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [appToDelete, setAppToDelete] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [localError, setLocalError] = useState(null);
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // We don't need to fetch apps here since the AppContext already does that
  // and we're using the apps from the context

  const handleCreateApp = async (e) => {
    e.preventDefault();
    if (!newAppName.trim()) {
      return;
    }

    try {
      setCreatingApp(true);
      const response = await AppService.createApp(newAppName);
      
      // Add the new app to the context
      addApp({
        id: response.id,
        name: response.name,
        apiKey: response.apiKey,
        createdAt: new Date().toISOString()
      });
      
      // Reset form and close modal
      setNewAppName('');
      setShowCreateModal(false);
      
      // Show the API key modal
      setSelectedApp(response);
      setShowApiKeyModal(true);
    } catch (err) {
      setLocalError('Failed to create app: ' + (err.response?.data || err.message));
    } finally {
      setCreatingApp(false);
    }
  };

  const handleDeleteApp = (app) => {
    setAppToDelete(app);
    setDeleteConfirmName('');
    setShowDeleteModal(true);
  };

  const confirmDeleteApp = async () => {
    // Check if the entered name matches the app name
    if (deleteConfirmName !== appToDelete.name) {
      setLocalError('App name does not match. Deletion canceled.');
      return;
    }

    try {
      setDeletingAppId(appToDelete.id);
      await AppService.deleteApp(appToDelete.id);
      
      // Remove the deleted app from the context
      removeApp(appToDelete.id);
      
      // Close the modal
      setShowDeleteModal(false);
      setAppToDelete(null);
      setDeleteConfirmName('');
    } catch (err) {
      setLocalError('Failed to delete app: ' + (err.response?.data || err.message));
    } finally {
      setDeletingAppId(null);
    }
  };

  const handleShowApiKey = (app) => {
    setSelectedApp(app);
    setShowApiKeyModal(true);
  };

  const copyApiKey = () => {
    if (selectedApp) {
      navigator.clipboard.writeText(selectedApp.apiKey)
        .then(() => {
          alert('API key copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy API key:', err);
        });
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex">
            <h2>Your Apps</h2>
            <div>
              <Button
                variant="primary"
                size="lg"
                onClick={() => setShowCreateModal(true)}
                className="ms-3"
              >
                Create New App <i className="bi bi-plus"></i>
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {(error || localError) && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">{localError || error}</Alert>
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : apps.length === 0 ? (
        <Alert variant="info">
          You don't have any apps yet. Click "Create New App" to get started.
        </Alert>
      ) : (
        <Row>
          {apps.map(app => (
            <Col key={app.id} md={4} className="mb-4">
              <Card className="app-card h-100">
                <Card.Body>
                  <Card.Title>{app.name}</Card.Title>
                  <Card.Text>
                    <strong>Created:</strong> {formatDate(app.createdAt)}
                  </Card.Text>
                  <div className="d-grid gap-2">
                    <Button 
                      variant="primary" 
                      onClick={() => handleShowApiKey(app)}
                    >
                      View API Key
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => handleDeleteApp(app)}
                      disabled={deletingAppId === app.id}
                    >
                      {deletingAppId === app.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Delete App'
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Create App Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New App</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateApp}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>App Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter app name"
                value={newAppName}
                onChange={(e) => setNewAppName(e.target.value)}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={creatingApp || !newAppName.trim()}
            >
              {creatingApp ? (
                <Spinner animation="border" size="sm" />
              ) : (
                'Create App'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* API Key Modal */}
      <Modal show={showApiKeyModal} onHide={() => setShowApiKeyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>API Key for {selectedApp?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-danger">
            <strong>Important:</strong> This is the only time you'll see this API key. Please copy it now and store it securely.
          </p>
          <div className="api-key-container">
            <code>{selectedApp?.apiKey}</code>
          </div>
          <Button 
            variant="outline-primary" 
            className="mt-3 w-100"
            onClick={copyApiKey}
          >
            Copy to Clipboard
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApiKeyModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete App Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm App Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-danger">
            <strong>Warning:</strong> This action cannot be undone. All data associated with this app will be permanently deleted.
          </p>
          <p>
            To confirm deletion, please enter the app name <strong>"{appToDelete?.name}"</strong> below:
          </p>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Enter app name to confirm"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              isInvalid={deleteConfirmName && deleteConfirmName !== appToDelete?.name}
            />
            {deleteConfirmName && deleteConfirmName !== appToDelete?.name && (
              <Form.Control.Feedback type="invalid">
                App name does not match
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDeleteApp}
            disabled={deleteConfirmName !== appToDelete?.name || deletingAppId === appToDelete?.id}
          >
            {deletingAppId === appToDelete?.id ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Delete App'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Apps;